import clsx from 'clsx';
import React, { Fragment } from 'react';
import { SanityBlock, SanityFigure, SanityLink } from '../../../../graphql-types';
import { Typography } from '../atoms/typography';
import { RichText } from '../molecules/rich-text';
import { Container } from '../atoms/container';
import { SanityImage } from '../molecules/sanity-image';
import { formatLink } from '../../../helpers/format-link';
import { DynamicLink } from '../molecules/dynamic-link';
import { useLanguage } from '../../../i18n';

export type Layout = 'image-left' | 'no-image' | 'image-right' | 'image-full';

type HeroBlockProps = {
    className?: string;
    title: string;
    description?: SanityBlock[];
    links?: SanityLink[];
    layout?: Layout;
    titleLevel?: 'h1' | 'h2';
    image?: SanityFigure;
    isImageDark?: boolean;
};

export function HeroBlock({
    title,
    description,
    links,
    image,
    isImageDark,
    className,
    layout,
    titleLevel = 'h1',
}: HeroBlockProps) {
    const titleParts = title.split('#');

    const hasImage = !!image?.asset?.url;
    const isImageLeft = layout === 'image-left' && hasImage;
    const isImageRight = layout === 'image-right' && hasImage;
    const fullImage = layout === 'image-full' && hasImage;
    const noImage = layout === 'no-image';

    const isTextLight = fullImage && isImageDark;

    const hasSideImage = isImageLeft || isImageRight;
    const language = useLanguage();

    let buttonCount = 0;

    const formattedLinks = links
        ?.map(link => formatLink({ ...link, isBackgroundDark: isTextLight, language }))
        .filter(Boolean);

    const linksWithCorrectButtonColor = formattedLinks?.map(item => {
        if (item.appearance === 'link') {
            return item;
        }
        if (buttonCount % 2 === 0) {
            buttonCount++;

            return item;
        }
        buttonCount++;

        return {
            ...item,
            variant: isTextLight ? ('neutral-light' as const) : ('neutral-dark' as const),
        };
    });

    return (
        <section
            className={clsx(className, 'relative overflow-hidden w-full', {
                'text-white': isTextLight,
                'text-neutral-900': !isTextLight,
            })}
        >
            <Container
                className={clsx({
                    'lg:grid lg:grid-cols-2': hasSideImage,
                    'md:py-14 lg:py-20': noImage,
                    'md:py-20 lg:py-28': fullImage,
                    'py-10': !hasSideImage,
                    'aspect-[10/9] sm:aspect-video lg:aspect-[2/1] lg:max-h-[85vh] grid items-center':
                        fullImage,
                })}
            >
                {hasSideImage || fullImage ? (
                    <div
                        className={clsx({
                            'absolute inset-0': fullImage,
                            '-mx-4 md:-mx-8 lg:mx-0 lg:-ml-16 xl:-ml-[calc((100vw-(100%*2))/2)]':
                                isImageLeft,
                            '-mx-4 md:-mx-8 lg:mx-0 lg:-mr-16 xl:-mr-[calc((100vw-(100%*2))/2)] lg:col-start-2':
                                isImageRight,
                        })}
                    >
                        <SanityImage
                            className={clsx('object-cover w-full', {
                                'aspect-[10/9] sm:aspect-video lg:aspect-square lg:h-full w-full lg:max-h-[85vh]':
                                    hasSideImage,
                                'h-full': fullImage,
                            })}
                            image={image}
                            sizes={hasSideImage ? '(max-width: 1024px) 100vw, 50vw' : '100vw'}
                        />
                    </div>
                ) : null}
                <div
                    className={clsx({
                        'py-6 md:py-10 lg:py-12 xl:py-20  lg:flex lg:flex-col lg:justify-center':
                            hasSideImage,
                        'lg:pl-16 xl:pl-20': isImageLeft,
                        'lg:col-start-1 lg:row-start-1 lg:pr-16 xl:pr-20': isImageRight,
                        'text-center relative md:max-w-2xl lg:max-w-4xl md:mx-auto': !hasSideImage,
                    })}
                >
                    <Typography
                        variant={'hd-3xl'}
                        desktopVariant={'hd-4xl'}
                        as={titleLevel}
                        className="whitespace-normal m-0"
                    >
                        {titleParts.map((part, index) => {
                            if (index % 2 === 0) {
                                return <Fragment key={index}>{part}</Fragment>;
                            }

                            return (
                                <span
                                    key={index}
                                    className={clsx({
                                        'text-primary-300': isTextLight,
                                        'text-primary-500': !isTextLight,
                                    })}
                                >
                                    {part}
                                </span>
                            );
                        })}
                    </Typography>
                    {description ? (
                        <RichText
                            value={description}
                            className={clsx({
                                'mt-4': hasSideImage,
                                'mt-6': !hasSideImage,
                            })}
                        />
                    ) : null}
                    {linksWithCorrectButtonColor && linksWithCorrectButtonColor.length > 0 ? (
                        <ul
                            className={clsx(
                                'mt-10 flex flex-col gap-y-1 md:flex-row md:items-center md:gap-x-4',
                                {
                                    'md:justify-center': !hasSideImage,
                                }
                            )}
                        >
                            {linksWithCorrectButtonColor.map((link, index) => {
                                if (!link) {
                                    return null;
                                }
                                const appearance = link.appearance || 'button';

                                return (
                                    <li key={index}>
                                        <DynamicLink
                                            {...link}
                                            size="md-lg"
                                            className={clsx(' md:w-auto ', {
                                                'w-full': appearance === 'button',
                                            })}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </div>
            </Container>
        </section>
    );
}
